import React, {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import '../../css/TableRowActions.css';
import {
    CalendarIcon,
    CheckIcon,
    ClockIcon,
    EyeIcon,
    FileTextIcon,
    CreditCardIcon,
    MapPinIcon,
    MoreVerticalIcon,
    NewFileIcon,
    PencilIcon,
    PrinterIcon,
    SearchIcon,
    UndoCircleIcon,
    WhatsAppIcon,
    XIcon,
} from './actionIcons';
import ActionIconButton from './ActionIconButton';
import AvailabilityButton from './AvailabilityButton';
import ConfirmButton from './ConfirmButton';
import ReverseButton from './ReverseButton';
import DeleteButton from './DeleteButton';
import EditButton from './EditButton';
import NewButton from './NewButton';
import SearchButton from './SearchButton';
import {buildDefaultWhatsAppMessage, buildWhatsAppUrl} from '../../utils/whatsappUtils';
import {focusCurrentTarget} from '../../utils/focusUtils';

const BUTTON_BY_TYPE = {
    new: NewButton,
    edit: EditButton,
    delete: DeleteButton,
    confirm: ConfirmButton,
    reverse: ReverseButton,
    search: SearchButton,
    availability: AvailabilityButton,
};

const ICON_BY_TYPE = {
    new: NewFileIcon,
    edit: PencilIcon,
    delete: XIcon,
    confirm: CheckIcon,
    search: SearchIcon,
    availability: CalendarIcon,
};

const ICON_BY_KEY = {
    view: EyeIcon,
    'view-customer': EyeIcon,
    efetivar: CheckIcon,
    contrato: FileTextIcon,
    entrega: FileTextIcon,
    term: FileTextIcon,
    maps: MapPinIcon,
    time: ClockIcon,
    extend: CalendarIcon,
    whatsapp: WhatsAppIcon,
    payments: CreditCardIcon,
    print: PrinterIcon,
    file: FileTextIcon,
};

const MENU_VARIANT_BY_TYPE = {
    new: 'new',
    edit: 'edit',
    delete: 'delete',
    confirm: 'confirm',
    reverse: 'finish',
    search: 'search',
    availability: 'availability',
};

const MENU_VARIANT_BY_KEY = {
    view: 'edit',
    'view-customer': 'edit',
    efetivar: 'confirm',
    contrato: 'confirm',
    entrega: 'confirm',
    term: 'confirm',
    maps: 'search',
    time: 'availability',
    extend: 'availability',
    whatsapp: 'whatsapp',
    payments: 'payments',
    print: 'print',
    file: 'term',
};

const getMenuVariant = (action) => {
    const extraClassName = action.buttonProps?.className || '';

    if (action.type === 'reverse' || action.label === 'Estornar' || action.label === 'Devolver') {
        return 'finish';
    }
    if (extraClassName.includes('finish-button')) {
        return 'finish';
    }
    if (extraClassName.includes('remove-button')) {
        return 'delete';
    }

    if (action.type && MENU_VARIANT_BY_TYPE[action.type]) {
        return MENU_VARIANT_BY_TYPE[action.type];
    }
    if (action.key && MENU_VARIANT_BY_KEY[action.key]) {
        return MENU_VARIANT_BY_KEY[action.key];
    }

    return 'confirm';
};

const MENU_VIEWPORT_PADDING = 8;
const MENU_TRIGGER_GAP = 4;

const TableRowActions = ({actions = [], breakpoint = 768, className = ''}) => {
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [isCompact, setIsCompact] = useState(() => window.innerWidth < breakpoint);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({
        top: 0,
        left: 0,
        maxHeight: undefined,
        transform: 'translateX(-100%)',
    });

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
        const updateCompact = () => setIsCompact(mediaQuery.matches);
        updateCompact();
        mediaQuery.addEventListener('change', updateCompact);
        return () => mediaQuery.removeEventListener('change', updateCompact);
    }, [breakpoint]);

    const updateMenuPosition = useCallback(() => {
        if (!triggerRef.current) {
            return;
        }
        const rect = triggerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom - MENU_TRIGGER_GAP - MENU_VIEWPORT_PADDING;
        const spaceAbove = rect.top - MENU_TRIGGER_GAP - MENU_VIEWPORT_PADDING;
        const openBelow = spaceBelow >= spaceAbove;
        const availableHeight = Math.max(
            88,
            Math.floor(openBelow ? spaceBelow : spaceAbove),
        );

        setMenuStyle({
            top: openBelow ? rect.bottom + MENU_TRIGGER_GAP : rect.top - MENU_TRIGGER_GAP,
            left: rect.right,
            maxHeight: availableHeight,
            transform: openBelow ? 'translate(-100%, 0)' : 'translate(-100%, -100%)',
        });
    }, []);

    useEffect(() => {
        if (!menuOpen) {
            return undefined;
        }

        updateMenuPosition();

        const handleOutside = (event) => {
            const target = event.target;
            if (
                containerRef.current?.contains(target) ||
                menuRef.current?.contains(target)
            ) {
                return;
            }
            setMenuOpen(false);
        };

        const handleReposition = () => updateMenuPosition();

        const listenerId = window.setTimeout(() => {
            document.addEventListener('mousedown', handleOutside);
        }, 0);

        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);

        return () => {
            window.clearTimeout(listenerId);
            document.removeEventListener('mousedown', handleOutside);
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [menuOpen, updateMenuPosition]);

    const visibleActions = actions.filter((action) => action.visible !== false);

    if (visibleActions.length === 0) {
        return null;
    }

    const getActionIcon = (action) => {
        if (action.icon) {
            return action.icon;
        }
        if (action.label === 'Estornar' || action.label === 'Devolver' || action.type === 'reverse') {
            return UndoCircleIcon;
        }
        if (action.type && ICON_BY_TYPE[action.type]) {
            return ICON_BY_TYPE[action.type];
        }
        if (action.key && ICON_BY_KEY[action.key]) {
            return ICON_BY_KEY[action.key];
        }
        return null;
    };

    const getWhatsAppUrl = (action) => {
        if (!action.phoneNumber) {
            return null;
        }
        const message = action.message ?? buildDefaultWhatsAppMessage(action.personName);
        return buildWhatsAppUrl(action.phoneNumber, message);
    };

    const runAction = (action) => {
        if (action.disabled) {
            return;
        }

        if (typeof action.onClick === 'function') {
            action.onClick();
        }

        setMenuOpen(false);
    };

    const renderActionButton = (action) => {
        if (action.render) {
            return action.render();
        }

        const ButtonComponent = BUTTON_BY_TYPE[action.type];
        if (ButtonComponent) {
            return (
                <ButtonComponent
                    title={action.label}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    {...(action.buttonProps || {})}
                />
            );
        }

        if (action.key && ICON_BY_KEY[action.key]) {
            return (
                <ActionIconButton
                    variant={action.key}
                    title={action.label}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    {...(action.buttonProps || {})}
                />
            );
        }

        return null;
    };

    const renderMenuIcon = (action) => {
        const Icon = getActionIcon(action);
        if (!Icon) {
            return null;
        }
        return (
            <span className="table-row-actions-menu-item__icon" aria-hidden="true">
                <Icon/>
            </span>
        );
    };

    const toggleMenu = (event) => {
        event.stopPropagation();
        focusCurrentTarget(event);
        if (menuOpen) {
            setMenuOpen(false);
            return;
        }
        updateMenuPosition();
        setMenuOpen(true);
    };

    const menuPortal = menuOpen && createPortal(
        <ul
            ref={menuRef}
            className="table-row-actions-menu"
            role="menu"
            style={{
                position: 'fixed',
                top: menuStyle.top,
                left: menuStyle.left,
                maxHeight: menuStyle.maxHeight,
                transform: menuStyle.transform,
            }}
        >
            {visibleActions.map((action) => {
                const whatsappUrl = action.key === 'whatsapp' ? getWhatsAppUrl(action) : null;
                const menuItemClassName = `table-row-actions-menu-item table-row-actions-menu-item--${getMenuVariant(action)}`;

                if (whatsappUrl) {
                    return (
                        <li key={action.key} role="none">
                            <a
                                href={whatsappUrl}
                                role="menuitem"
                                className={menuItemClassName}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    focusCurrentTarget(event);
                                    setMenuOpen(false);
                                }}
                            >
                                {renderMenuIcon(action)}
                                <span className="table-row-actions-menu-item__label">{action.label}</span>
                            </a>
                        </li>
                    );
                }

                return (
                    <li key={action.key} role="none">
                        <button
                            type="button"
                            role="menuitem"
                            className={menuItemClassName}
                            disabled={action.disabled}
                            onClick={(event) => {
                                event.stopPropagation();
                                focusCurrentTarget(event);
                                runAction(action);
                            }}
                        >
                            {renderMenuIcon(action)}
                            <span className="table-row-actions-menu-item__label">{action.label}</span>
                        </button>
                    </li>
                );
            })}
        </ul>,
        document.body,
    );

    if (isCompact) {
        return (
            <>
                <div
                    ref={containerRef}
                    className={`table-row-actions table-row-actions--compact ${className}`.trim()}
                >
                    <button
                        ref={triggerRef}
                        type="button"
                        className="table-row-actions-menu-trigger action-icon-button"
                        title="Ações"
                        aria-label="Ações"
                        aria-expanded={menuOpen}
                        aria-haspopup="menu"
                        onClick={toggleMenu}
                    >
                        <MoreVerticalIcon/>
                    </button>
                </div>
                {menuPortal}
            </>
        );
    }

    return (
        <div
            ref={containerRef}
            className={`table-row-actions table-row-actions--inline ${className}`.trim()}
        >
            {visibleActions.map((action) => (
                <span key={action.key} className="table-row-actions-item">
                    {renderActionButton(action)}
                </span>
            ))}
        </div>
    );
};

export default TableRowActions;
